import React, { useEffect, useState } from "react";
import { money } from "@/lib/money";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MapPin, Calendar, DollarSign, CheckCircle2, MessageSquare, Star, ShieldCheck, Sparkles, Loader2, X, AlertCircle, UserCheck, Users, ChevronDown, ChevronRight, Trash2, Pencil } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import PaymentHandleList from "@/components/profile/PaymentHandleList";
import InvoicePanel from "@/components/invoices/InvoicePanel";
import api, { mediaUrl } from "@/lib/api";
import { armFeedbackPrompt } from "@/lib/feedbackPrompt";
import JobPhotoGrid, { MAX_JOB_PHOTOS } from "@/components/uploads/JobPhotoGrid";
import JobPhotoGallery from "@/components/uploads/JobPhotoGallery";
import { errorMessage, errorCode, aiErrorText } from "@/lib/errors";
import { categoryLabel, subcategoryLabel } from "@/lib/catalog";
import { useAuth } from "@/contexts/AuthContext";
import { budgetText, jobLocationText } from "@/lib/jobDisplay";
import { editJobPath, messagesPath, ownJobsPath } from "@/lib/ownerRoutes";
import { backTarget, from } from "@/lib/cameFrom";
import { QUOTE_REPLACEABLE, quoteStatusClass, quoteStatusLabel } from "@/lib/quoteStatus";
import { MODE_PROVIDER } from "@/lib/dany";
import { DanyAvatar } from "@/ds/dany/DanyAvatar";
import { formatDate } from "@/lib/dates";

export default function JobDetail() {
  const { t } = useTranslation();
  const { jobId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [job, setJob] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  // Unrated, not five stars (R11, 2026-09-17).
  //
  // This modal opened with all three dimensions already at the maximum, so the
  // path of least effort was Submit, and Submit meant a perfect score nobody
  // had given. A default that leans one way is not a default — it is an answer
  // filled in on the reviewer's behalf, and it inflates exactly the number the
  // platform asks people to trust. The first organic review on FixyDany
  // (Impresos YA, 17-sep) would have been one of those.
  //
  // `null` also states the thing the component has to know and could not:
  // whether a dimension has been rated at all. `POST /reviews` requires
  // ge=1 on all three (models/chat.py), so Submit stays disabled until it is
  // a request the server would accept.
  const [review, setReview] = useState({ quality: null, responsibility: null, price: null, review_text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [compLoading, setCompLoading] = useState(false);
  const [comparison, setComparison] = useState("");
  const [compError, setCompError] = useState("");
  // T9: quote_id awaiting the liability-disclaimer confirmation. The accept
  // request is only sent from the modal, never straight from the card button.
  const [disclaimerQuoteId, setDisclaimerQuoteId] = useState(null);
  // Photos after publishing (D1, deferred and closed 2026-08-20). Held apart
  // from `job` because the grid is edited freely and only the Save button
  // writes — the same shape the Dany widgets use, for the same reason.
  const [photoDraft, setPhotoDraft] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoErr, setPhotoErr] = useState("");
  const [photoSaved, setPhotoSaved] = useState(false);
  // Releasing a directed job to the whole marketplace ([FIX] 2026-09-07).
  const [openingToAll, setOpeningToAll] = useState(false);
  const [openToAllErr, setOpenToAllErr] = useState("");
  // The line items of the reader's own accepted quote, collapsed by default:
  // the number they were paid is the headline, the breakdown is the detail.
  const [myItemsOpen, setMyItemsOpen] = useState(false);
  // N44/N45. Withdrawing from this page, and the second tap that commits it.
  const [withdrawing, setWithdrawing] = useState(false);
  const [confirmWithdraw, setConfirmWithdraw] = useState(false);
  const [withdrawErr, setWithdrawErr] = useState("");
  // T19. The owner's three verbs on their own job: delete (open), edit
  // (open), cancel (in progress). Each confirm is a modal of its own.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const role = user?.profile?.role;
  // **Ownership, not role** (DY2.6). Every owner-side control on this page used
  // to hang off `role === "client"`, which was a true synonym for "this is my
  // job" only for as long as a FixyBee could not post one. Now it is not, and
  // the wrong half of the synonym would have shown a provider-owner a job with
  // no quotes on it, no Accept button and no way to review the FixyBee they
  // hired — while the server, which has always gated these by `client_id`,
  // would have answered every one of those requests happily.
  const isOwner = !!(job && user && job.client_id === user.user_id);
  // Exactly the pair the endpoint enforces. Showing the editor to anyone else
  // would be offering a button whose only possible answer is a refusal.
  const canEditPhotos = isOwner && job?.status === "open";
  // A job posted for one named FixyBee: private between the two of them, no
  // FixyMatch, and quotable by nobody else until its owner opens it.
  const directedName = job?.directed_provider_name || "";
  const isDirected = !!job?.directed_provider_id;
  const isDirectedToMe = isDirected && job.directed_provider_id === user?.user_id;
  // The reader's OWN quote on this job — never the competition, which is what
  // `GET /quotes?job_id=` returns and why it answers 403 to anyone but the
  // owner. Null for the owner and for a FixyBee who has not quoted yet.
  const myQuote = job?.my_quote || null;
  // An outdated quote (T19 X2) does not hold the job either: the requester
  // changed it, and the way forward is a new quote — free, server-side.
  const iQuoted = !!myQuote && !QUOTE_REPLACEABLE.includes(myQuote.status);
  // The chat thread on this job that the reader is a party to, if any. One id
  // for both sides: the requester's "Open chat" used to navigate to the inbox
  // with nothing named and land on whichever conversation was newest.
  const threadHref = job?.thread_id
    ? `${messagesPath(role)}?thread=${job.thread_id}`
    : messagesPath(role);

  // All three rated. The gate on Submit, and the same condition the endpoint
  // enforces — `quality`, `responsibility` and `price` are `ge=1` there, so an
  // unrated dimension is a 422 the reviewer would have had no way to read.
  const reviewComplete = ["quality", "responsibility", "price"]
    .every((k) => Number(review[k]) >= 1);

  const statusLabel = (s) => ({
    open: t("clientJobsX.statusOpen"),
    in_progress: t("clientJobsX.statusInProgress"),
    completed: t("clientJobsX.statusCompleted"),
    canceled: t("clientJobsX.statusCanceled"),
  }[s] || s);

  const load = async () => {
    const { data } = await api.get(`/jobs/${jobId}`);
    setJob(data);
    setPhotoDraft((data.photos || []).map((p) => p.url));
    // Read off the response rather than off `isOwner`, which is derived from
    // the `job` state this call is what sets — it is still null here.
    if (data.client_id === user.user_id) {
      const q = await api.get(`/quotes?job_id=${jobId}`);
      setQuotes(q.data.quotes || []);
    }
  };

  useEffect(() => { if (jobId) load(); }, [jobId]); // eslint-disable-line

  const openToAll = async () => {
    setOpenToAllErr("");
    setOpeningToAll(true);
    try {
      await api.post(`/jobs/${jobId}/open-to-all`);
      await load();
    } catch (e) {
      setOpenToAllErr(errorMessage(e, t("jobDetailX.openToAllFailed")));
    } finally {
      setOpeningToAll(false);
    }
  };

  const acceptQuote = async (quoteId) => {
    await api.post(`/quotes/${quoteId}/accept`, { accepted_disclaimer: true });
    setDisclaimerQuoteId(null);
    load();
  };
  const declineQuote = async (quoteId) => {
    await api.post(`/quotes/${quoteId}/decline`);
    load();
  };
  const savePhotos = async () => {
    setPhotoBusy(true);
    setPhotoErr("");
    setPhotoSaved(false);
    try {
      await api.patch(`/jobs/${jobId}`, { photo_urls: photoDraft || [] });
      await load();
      setPhotoSaved(true);
    } catch (e) {
      // The endpoint answers {code, message}. Translate the code; fall back to
      // the server's English only for something we have no string for.
      const code = errorCode(e);
      setPhotoErr(
        ["job_photos_max", "job_not_yours", "job_not_open"].includes(code)
          ? t(`jobPhotos.errors.${code}`, { max: MAX_JOB_PHOTOS })
          : errorMessage(e, t("jobPhotos.errors.generic"))
      );
    } finally {
      setPhotoBusy(false);
    }
  };

  /**
   * N44. Withdraw the quote from the page the FixyBee is actually on.
   *
   * The endpoint and the notice to the requester have existed since N25 and
   * were reachable from exactly one place: the Quotes list. So a FixyBee who
   * opened the job to see what they had sent — which is the page they reach
   * from the notification, from Dany, and from "Requests for you" — found the
   * quote block, no way out of it, and Dany naming a page they would have to
   * go and find. Same endpoint, same notice, same copy as the list: this is a
   * second door onto one action, not a second action.
   */
  const withdrawMyQuote = async () => {
    if (!myQuote?.quote_id) return;
    setWithdrawErr("");
    setWithdrawing(true);
    try {
      await api.post(`/quotes/${myQuote.quote_id}/withdraw`);
      await load();
    } catch {
      setWithdrawErr(t("providerQuotesX.withdrawFailed"));
    } finally {
      setWithdrawing(false);
    }
  };

  /**
   * T19 F1. Delete an open post. The server closes every quote on it and
   * tells each FixyBee in the same request; this page then has nothing left to
   * show, so it goes — to the requester's own list, named rather than
   * `navigate(-1)`, and with `replace` so Back cannot return to a job that
   * answers 404 now.
   */
  const deleteJob = async () => {
    await api.delete(`/jobs/${jobId}`);
    navigate(ownJobsPath(role), { replace: true });
  };

  /**
   * T19 F2 (X1). Cancel a job in progress — the requester's call alone, with a
   * reason the FixyBee reads in the bell, the mail and the chat. The bounds
   * are the endpoint's (10-500, on the trimmed text); the button below holds
   * itself until they are met, so the 400 is a rule and not a surprise.
   */
  const reasonLength = cancelReason.trim().replace(/\s+/g, " ").length;
  const reasonOk = reasonLength >= 10 && reasonLength <= 500;
  const cancelJob = async () => {
    await api.post(`/jobs/${jobId}/cancel`, { reason: cancelReason });
    setCancelOpen(false);
    setCancelReason("");
    await load();
  };

  const markComplete = async () => {
    await api.post(`/jobs/${jobId}/complete`);
    setReviewOpen(true);
    load();
  };
  const submitReview = async () => {
    if (!reviewComplete) return;
    setSubmitting(true);
    try {
      await api.post(`/reviews`, { job_id: jobId, ...review });
      setReviewOpen(false);
      // T15. The client's other moment — whichever of a first job and a first
      // review comes first, and the server's one-shot gate is what makes
      // "whichever" true without either page counting anything. Armed AFTER
      // `setReviewOpen(false)` so the survey can never stack on the review
      // modal it was asked from.
      armFeedbackPrompt();
      load();
    } finally { setSubmitting(false); }
  };

  const compareWithAI = async () => {
    setCompError("");
    setCompLoading(true);
    try {
      const { data } = await api.post("/ai/comparator", { job_id: jobId });
      setComparison(data?.comparison || "—");
    } catch (e) {
      setCompError(aiErrorText(e, t, t("jobDetailX.compErr")));
    } finally {
      setCompLoading(false);
    }
  };

  if (!job) return <DashboardLayout role={role}><div className="skeleton h-40 w-full" /></DashboardLayout>;

  const acceptedQuote = quotes.find((q) => q.status === "accepted");
  const dims = [
    { key: "quality", label: t("jobDetailX.quality") },
    { key: "responsibility", label: t("jobDetailX.responsibility") },
    { key: "price", label: t("jobDetailX.price") },
  ];

  return (
    <DashboardLayout role={role}>
      {/* N1. Back to the screen they actually came from, which the linking
          surface told us — "My quotes", the board, their own list, the
          directed-requests inbox. It used to be worked out from role and
          ownership alone, and those two cannot tell the two ways a FixyBee
          reaches this page apart: opening their own quote put them back on the
          board, with the list and its filter gone.

          The fallback is that old rule, unchanged, and it is what answers for
          an arrival with nothing behind it — a push notification, an email
          link, a pasted URL. Never `navigate(-1)`: see lib/cameFrom.js. */}
      <button onClick={() => navigate(backTarget(
                location,
                !isOwner && role === "provider"
                  ? "/provider/browse" : ownJobsPath(role)))}
              className="fx-btn fx-btn--ghost mb-4" data-testid="back-btn">
        <ArrowLeft size={16} /> {t("jobDetailX.back")}
      </button>

      <div className="card-base p-6" data-testid="job-detail">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <span className={`badge ${
              job.status === "open" ? "bg-hive text-worker" :
              job.status === "in_progress" ? "bg-info text-cream" :
              job.status === "completed" ? "bg-success text-cream" :
              "bg-sand"
            }`}>{statusLabel(job.status)}</span>
            <h1 className="font-display text-3xl font-bold text-worker mt-3">{job.title}</h1>
            <div className="mt-2 flex gap-4 flex-wrap text-sm text-smoke">
              {jobLocationText(job) && (
                <span className="flex items-center gap-1"><MapPin size={14} />{jobLocationText(job)}</span>
              )}
              <span className="flex items-center gap-1 font-mono"><DollarSign size={14} />{budgetText(t, job.budget_min, job.budget_max)}</span>
              <span className="flex items-center gap-1 font-mono"><Calendar size={14} />{formatDate(job.created_at)}</span>
            </div>
          </div>
          {job.category_name && (
            <span className="chip" data-testid="job-category-chip">
              {categoryLabel(t, job)}
              {job.subcategory_name ? ` • ${subcategoryLabel(t, job)}` : ""}
            </span>
          )}
        </div>

        <p className="mt-5 text-worker leading-relaxed whitespace-pre-wrap">{job.description}</p>

        {canEditPhotos ? (
          <div className="mt-5" data-testid="job-photos-editor">
            <div className="flex items-center justify-between mb-1">
              <label className="label-base mb-0">{t("jobPhotos.title")}</label>
              <span className="text-xs text-smoke font-mono">
                {(photoDraft || []).length}/{MAX_JOB_PHOTOS}
              </span>
            </div>
            <p className="text-xs text-smoke mb-3">{t("jobPhotos.hint")}</p>
            <JobPhotoGrid
              urls={photoDraft || []}
              onChange={(next) => { setPhotoDraft(next); setPhotoSaved(false); setPhotoErr(""); }}
              disabled={photoBusy}
              testid="job-detail-photo"
            />
            {photoErr && (
              <p className="mt-2 text-xs text-red-700 flex items-start gap-1" data-testid="job-photos-error">
                <AlertCircle size={13} className="shrink-0 mt-px" />{photoErr}
              </p>
            )}
            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={savePhotos}
                disabled={photoBusy}
                className="fx-btn fx-btn--primary text-xs px-3 py-2"
                data-testid="job-photos-save"
              >
                {photoBusy ? <Loader2 size={13} className="inline mr-1 animate-spin" /> : null}
                {photoBusy ? t("jobPhotos.saving") : t("jobPhotos.save")}
              </button>
              {photoSaved && (
                <span className="text-xs text-smoke inline-flex items-center gap-1" data-testid="job-photos-saved">
                  <CheckCircle2 size={13} />{t("jobPhotos.saved")}
                </span>
              )}
            </div>
          </div>
        ) : job.photos?.length > 0 ? (
          /* Was a grid of `target="_blank"` links: on a phone a tap left the
             app for a bare image on a white browser tab, and the way back was
             the browser's, not the job's. */
          <div className="mt-5">
            <JobPhotoGallery urls={job.photos.map((p) => p.url)} testid="job-photos" />
          </div>
        ) : null}

        {/* T19. What the owner can still do to a post nobody has been hired
            for. Exactly the condition the endpoints enforce — `DELETE` and
            `PATCH` refuse anything that is not open — so neither button is
            ever offered only to be refused. */}
        {isOwner && job.status === "open" && (
          <div className="mt-6 pt-4 border-t border-sand flex flex-wrap gap-2" data-testid="job-owner-actions">
            <Link
              to={editJobPath(role, jobId)}
              state={from(location.pathname)}
              className="fx-btn fx-btn--secondary text-sm"
              data-testid="job-edit-cta"
            >
              <Pencil size={16} /> {t("jobDetailX.editJob")}
            </Link>
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="fx-btn fx-btn--ghost text-sm text-danger"
              data-testid="job-delete-cta"
            >
              <Trash2 size={16} /> {t("jobDetailX.deletePost")}
            </button>
          </div>
        )}
      </div>

      {/* **Who is asking.** `client_snippet` has been in this payload since the
          Postgres migration — `GET /jobs/{id}` resolves it for everyone allowed
          to read the job, and the smoke has asserted it since then — and no job
          page had ever rendered it. It was not lost: it was never drawn. So a
          FixyBee opening a request, directed or from the board, read a job with
          no one attached to it and had to guess from the description.

          Display name and city only. Deliberately no email and no phone: the
          coordination channel is the in-app chat, and it opens when a quote is
          accepted, not when a request is read. */}
      {!isOwner && job.client_snippet?.full_name && (
        <div className="mt-6 card-base p-4 max-w-2xl flex items-center gap-3" data-testid="job-requester">
          <div className="w-10 h-10 rounded-full bg-hive flex items-center justify-center font-display font-bold text-worker shrink-0 overflow-hidden">
            {job.client_snippet.avatar_url
              ? <img src={mediaUrl(job.client_snippet.avatar_url)} alt="" className="w-full h-full object-cover" />
              : job.client_snippet.full_name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-smoke">{t("jobDetailX.requestedBy")}</p>
            <p className="font-display font-bold text-worker truncate" data-testid="job-requester-name">
              {job.client_snippet.full_name}
            </p>
            {job.client_snippet.city && (
              <p className="text-xs text-smoke flex items-center gap-1 mt-0.5">
                <MapPin size={11} />{job.client_snippet.city}
              </p>
            )}
          </div>
        </div>
      )}

      {/* A directed job, seen by the person who directed it. The button is the
          only exit it has — there is no expiry (Jorge, 2026-09-07) — so if the
          FixyBee never answers, this is what the requester is left holding. */}
      {isOwner && isDirected && job.status === "open" && (
        <div className="mt-6 card-base p-4 max-w-2xl" data-testid="directed-owner-banner">
          <p className="text-sm text-worker flex items-start gap-2">
            <UserCheck size={16} className="text-success mt-0.5 shrink-0" />
            <span>{t("jobDetailX.directedOwner", { name: directedName })}</span>
          </p>
          <button
            onClick={openToAll}
            disabled={openingToAll}
            className="fx-btn fx-btn--secondary mt-3 text-xs inline-flex"
            data-testid="open-to-all-btn"
          >
            {openingToAll ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />}
            {t("jobDetailX.openToAll")}
          </button>
          <p className="text-xs text-smoke mt-2">{t("jobDetailX.openToAllHint")}</p>
          {openToAllErr && (
            <p className="text-xs text-danger mt-2" data-testid="open-to-all-error">{openToAllErr}</p>
          )}
        </div>
      )}

      {/* The same job, seen by the FixyBee it was sent to. Their push said "a
          client asked you"; the page has to agree with the push. */}
      {isDirectedToMe && !isOwner && job.status === "open" && (
        <div className="mt-6 card-base p-4 max-w-2xl" data-testid="directed-provider-banner">
          <p className="text-sm text-worker flex items-start gap-2">
            <UserCheck size={16} className="text-success mt-0.5 shrink-0" />
            <span>
              {job.client_snippet?.full_name
                ? t("jobDetailX.directedToYouBy", { name: job.client_snippet.full_name })
                : t("jobDetailX.directedToYou")}
            </span>
          </p>
        </div>
      )}

      {/* `!isOwner` is the UI half of the rule; the server half is the 403 in
          `routes.quotes.send_quote`, and that one is the rule. Hiding the
          button is only so nobody is offered something whose only possible
          answer is a refusal. `!iQuoted` joins it for the same reason: a second
          quote on the same job is a 400 from the same endpoint, and finding
          that out at the Send button is finding out too late. What takes its
          place is the block below — their own quote, with its state. */}
      {role === "provider" && !isOwner && !iQuoted && job.status === "open" && (
        /*
          Two doors to the same quote, and only one of them is gold.
          "One gold button per screen. If there are two, there is a bug" is the
          design system's rule (src/ds/index.js), and it settles the hierarchy
          question R7 asked rather than leaving it to taste: the classic form
          keeps the primary, Dany's door is the secondary beside it. What makes
          the second one legible is not weight but her face — the avatar says
          whose door it is, which no amount of gold would have said.
          The line underneath is the whole reason to press it: the generic FAB
          could never say that Dany will price THIS job with them.
        */
        <div className="mt-6" data-testid="quote-cta-row">
          <div className="flex flex-wrap items-center gap-3">
            <Link to={`/provider/quotes/new?job=${jobId}`}
                  state={from(`/provider/jobs/${jobId}`)}
                  className="fx-btn fx-btn--primary inline-flex" data-testid="send-quote-cta">
              {t("jobDetailX.sendQuoteCta")}
            </Link>
            <button
              type="button"
              onClick={() => navigate("/dany", {
                state: {
                  mode: MODE_PROVIDER,
                  jobId,
                  seed: t("jobDetailX.quoteWithDanySeed", { title: job.title }),
                },
              })}
              className="fx-btn fx-btn--secondary inline-flex"
              data-testid="quote-with-dany-cta"
            >
              <DanyAvatar size={22} state="idle" halo={false} />
              {t("jobDetailX.quoteWithDany")}
            </button>
          </div>
          <p className="text-xs text-smoke mt-2 max-w-md">{t("jobDetailX.quoteWithDanyHint")}</p>
        </div>
      )}

      {/* The FixyBee's side of the job, which had no side at all: every quote
          on this page hung off `isOwner`, so a provider who had quoted — or
          won — saw the job description and stopped there. What they quoted,
          what became of it, who they are dealing with, and the way into the
          conversation all live here. */}
      {myQuote && !isOwner && (
        <div
          className={`mt-8 card-base p-5 ${myQuote.status === "accepted" ? "border-success ring-2 ring-success/30 bg-cream" : ""}`}
          data-testid="my-quote-block"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h2 className="font-display text-xl font-bold text-worker">
                {myQuote.status === "accepted"
                  ? t("jobDetailX.myQuoteAccepted")
                  : t("jobDetailX.myQuoteTitle")}
              </h2>
              {myQuote.status === "accepted" && (
                <p className="text-sm text-smoke mt-1">{t("jobDetailX.myQuoteAcceptedHint")}</p>
              )}
            </div>
            <div className="text-right shrink-0">
              {/* The agreed number, not the requester's range: the range is a
                  wish and this is what was accepted. */}
              <p className="font-mono text-2xl font-bold text-worker" data-testid="my-quote-total">
                {money(myQuote.total_due, "$0.00")}
              </p>
              <span className={`badge mt-1 ${quoteStatusClass(myQuote.status)}`} data-testid="my-quote-status">
                {quoteStatusLabel(t, myQuote.status)}
              </span>
            </div>
          </div>

          {(() => {
            // Once accepted, the dates that count are the job's agreed ones —
            // the accept flow copies the proposal into them. Before that, the
            // proposal is all there is.
            const start = job.agreed_start_date || myQuote.proposed_start_date;
            const end = job.agreed_end_date || myQuote.proposed_end_date;
            return (start || end) ? (
              <p className="mt-3 text-sm text-smoke flex items-center gap-1 font-mono" data-testid="my-quote-dates">
                <Calendar size={14} />
                {[start, end].filter(Boolean).map((d) => formatDate(d)).join(" – ")}
              </p>
            ) : null;
          })()}

          {myQuote.line_items?.length > 0 && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setMyItemsOpen((v) => !v)}
                className="fx-btn fx-btn--ghost text-xs px-0"
                data-testid="my-quote-items-toggle"
              >
                {myItemsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {t("jobDetailX.myQuoteItems", { n: myQuote.line_items.length })}
              </button>
              {myItemsOpen && (
                <div className="mt-2 space-y-1 text-sm" data-testid="my-quote-items">
                  {myQuote.line_items.map((li, i) => (
                    <div key={i} className="flex justify-between text-smoke">
                      <span>{li.description} × <span className="num">{li.quantity}</span></span>
                      <span className="num">{money(li.subtotal, "$0.00")}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {myQuote.status === "accepted" && job.thread_id && (
            <Link to={threadHref} className="fx-btn fx-btn--primary mt-4 inline-flex" data-testid="my-quote-chat-cta">
              <MessageSquare size={16} /> {t("jobDetailX.goToConversation")}
            </Link>
          )}

          {/* PLANES M7: the FixyBee's invoice for the quote the client accepted. */}
          {myQuote.status === "accepted" && <InvoicePanel quoteId={myQuote.quote_id} />}

          {/* N44. Only on `sent`, the same condition the Quotes list uses and
              for the same reason: an accepted quote is a commitment, and a
              declined or withdrawn one has nothing left to withdraw. The
              server refuses all three, and a button that exists only to be
              refused is a worse answer than no button.

              N45. Two taps in line, the pattern the draft card already uses
              (`DanyJobDraftWidget`) rather than `window.confirm`: a native
              dialog is the one piece of this app that is not this app, it
              cannot be translated by i18next, and on a phone it arrives
              detached from the thing it is asking about. */}
          {(myQuote.status === "sent" || myQuote.status === "stale") && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <button
                type="button"
                className={confirmWithdraw
                  ? "fx-btn fx-btn--ghost text-sm bg-danger text-cream hover:bg-danger"
                  : "fx-btn fx-btn--ghost text-sm"}
                disabled={withdrawing}
                onClick={() => (confirmWithdraw
                  ? withdrawMyQuote()
                  : setConfirmWithdraw(true))}
                data-testid="my-quote-withdraw"
              >
                {withdrawing
                  ? t("providerQuotesX.withdrawing")
                  : confirmWithdraw
                    ? t("providerQuotesX.withdrawConfirmAction")
                    : t("providerQuotesX.withdraw")}
              </button>
              {confirmWithdraw && !withdrawing && (
                <button
                  type="button"
                  className="fx-btn fx-btn--ghost text-sm"
                  onClick={() => setConfirmWithdraw(false)}
                  data-testid="my-quote-withdraw-cancel"
                >
                  {t("providerQuotesX.withdrawKeep")}
                </button>
              )}
              {/* The consequence, stated once and beside the button that
                  carries it out — which is what the native dialog was for. */}
              {confirmWithdraw && !withdrawing && (
                <p className="text-xs text-smoke basis-full" data-testid="my-quote-withdraw-hint">
                  {t("providerQuotesX.withdrawConfirm")}
                </p>
              )}
            </div>
          )}
          {withdrawErr && (
            <p className="text-sm text-danger mt-2" data-testid="my-quote-withdraw-error">
              {withdrawErr}
            </p>
          )}
        </div>
      )}

      {isOwner && (
        <div className="mt-8">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h2 className="font-display text-2xl font-bold text-worker" data-testid="quotes-header">
              {t("jobDetailX.quotesHeader", { n: quotes.length })}
            </h2>
            {quotes.filter((q) => q.status !== "withdrawn").length >= 2 && job.status === "open" && (
              <button onClick={compareWithAI} disabled={compLoading} className="fx-btn fx-btn--ghost text-xs font-display" data-testid="ai-compare-btn">
                {compLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {t("jobDetailX.compareWithAi")}
              </button>
            )}
          </div>
          {comparison && (
            <div className="card-base p-4 mb-4 bg-cream border-hive whitespace-pre-wrap text-sm text-worker" data-testid="ai-comparison">
              <p className="font-display font-bold text-hive-ink mb-2 flex items-center gap-1"><Sparkles size={14} /> {t("jobDetailX.aiComparison")}</p>
              {comparison}
            </div>
          )}
          {compError && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-2 text-sm text-red-700 mb-3" data-testid="ai-compare-error">
              {compError}
            </div>
          )}
          {quotes.length === 0 ? (
            <p className="text-smoke">{t("jobDetailX.noQuotesYet")}</p>
          ) : (
            <div className="space-y-3" data-testid="quotes-list">
              {quotes.map((q) => {
                const p = q.provider_snippet || {};
                return (
                  <div key={q.quote_id} className={`card-base p-5 ${q.status === "accepted" ? "border-success ring-2 ring-success/30" : ""}`} data-testid={`quote-${q.quote_id}`}>
                    <div className="flex justify-between items-start gap-3 flex-wrap">
                      <div className="flex gap-3 items-center min-w-0">
                        <div className="w-12 h-12 rounded-full bg-hive flex items-center justify-center font-display font-bold text-worker shrink-0">
                          {(p.full_name || "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-display font-bold text-worker truncate">{p.company_name || p.full_name}</p>
                            {/* The trust signal a requester weighs quotes by
                                (PLANES M6): labelled, not a bare icon. An
                                approved identity verification, nothing else. */}
                            {p.is_verified && (
                              <span className="badge badge-verified text-[10px] shrink-0" data-testid={`quote-verified-${q.quote_id}`}>
                                <ShieldCheck size={12} /> {t("providers.verified")}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-smoke flex items-center gap-2">
                            {p.average_rating > 0 && (<><Star size={12} className="text-hive-ink fill-hive" /> {p.average_rating}</>)}
                            <span>· {p.completed_jobs_count || 0} {t("jobDetailX.jobsSuffix")}</span>
                            {q.public_slug && <Link to={`/pro/${q.public_slug}`} className="text-info hover:underline">{t("jobDetailX.viewProfile")}</Link>}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {/* N24 sweep. The number in the largest type on this
                            page is what the client pays. It was `grand_total`
                            — the line items with the tax left off — so the
                            figure a FixyBee had typed a rate onto appeared
                            nowhere the person deciding could see it. The
                            breakdown is under the line items, and only when
                            there is tax: a "Tax $0.00" line on a quote with no
                            tax is an invoice this platform did not write. */}
                        <p className="font-mono text-2xl font-bold text-worker"
                           data-testid={`quote-total-${q.quote_id}`}>
                          {money(q.total_due, "$0.00")}
                        </p>
                        <span className="badge mt-1">{quoteStatusLabel(t, q.status)}</span>
                      </div>
                    </div>

                    {q.line_items?.length > 0 && (
                      <div className="mt-4 space-y-1 text-sm">
                        {q.line_items.map((li, i) => (
                          <div key={i} className="flex justify-between text-smoke">
                            <span>{li.description} × <span className="num">{li.quantity}</span></span>
                            <span className="num">{money(li.subtotal, "$0.00")}</span>
                          </div>
                        ))}
                        {q.tax_rate !== null && q.tax_rate !== undefined && (
                          <div className="pt-1 border-t border-sand space-y-1"
                               data-testid={`quote-tax-${q.quote_id}`}>
                            <div className="flex justify-between text-smoke">
                              <span>{t("jobDetailX.subtotal")}</span>
                              <span className="num">{money(q.grand_total, "$0.00")}</span>
                            </div>
                            <div className="flex justify-between text-smoke">
                              <span>{t("jobDetailX.tax", { rate: q.tax_rate })}</span>
                              <span className="num">{money(q.tax_amount, "$0.00")}</span>
                            </div>
                            <div className="flex justify-between font-semibold text-worker">
                              <span>{t("jobDetailX.total")}</span>
                              <span className="num">{money(q.total_due, "$0.00")}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {q.payment_terms && <p className="mt-3 text-xs text-smoke"><strong>{t("jobDetailX.terms")}:</strong> {q.payment_terms}</p>}

                    {/* How this FixyBee gets paid (T12-M4c). Shown on the
                        ACCEPTED quote only: before that there is nothing to
                        pay, and a copyable payment address next to a quote
                        still being weighed invites paying for a job neither
                        side has agreed to. The handles are public on the
                        FixyBee's own page either way, so this hides no
                        information — it only stops offering the action at the
                        moment it would be wrong. Contact only: the payment
                        happens inside the app and FixyDany records none of it. */}
                    {q.status === "accepted" && p.payment_methods?.length > 0 && (
                      <div className="mt-4" data-testid={`quote-payments-${q.quote_id}`}>
                        <p className="text-xs text-smoke mb-2">{t("payouts.clientContext")}</p>
                        <PaymentHandleList methods={p.payment_methods} compact />
                      </div>
                    )}

                    {q.status === "sent" && job.status === "open" && (
                      <div className="mt-4 flex gap-2">
                        {/* Per-quote CTA in a list: secondary, so several quotes never put several gold buttons on one screen. */}
                        <button onClick={() => setDisclaimerQuoteId(q.quote_id)} className="fx-btn fx-btn--secondary text-sm" data-testid={`accept-${q.quote_id}`}>{t("jobDetailX.acceptQuote")}</button>
                        <button onClick={() => declineQuote(q.quote_id)} className="fx-btn fx-btn--ghost text-sm" data-testid={`decline-${q.quote_id}`}>{t("jobDetailX.decline")}</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isOwner && job.status === "in_progress" && acceptedQuote && (
        <div className="mt-8 card-base p-5 bg-cream">
          <h3 className="font-display font-bold text-worker">{t("jobDetailX.jobInProgress")}</h3>
          <p className="text-sm text-smoke mt-1">{t("jobDetailX.coordinateInChat")}</p>
          <div className="mt-4 flex gap-2 flex-wrap">
            <Link to={threadHref} className="fx-btn fx-btn--secondary text-sm" data-testid="open-chat-cta"><MessageSquare size={16} /> {t("jobDetailX.openChat")}</Link>
            <button onClick={markComplete} className="fx-btn fx-btn--primary text-sm" data-testid="mark-complete-btn">
              <CheckCircle2 size={16} /> {t("jobDetailX.markComplete")}
            </button>
          </div>
          {/* Apart from "Mark complete", on its own line and in the quiet
              style, because the two end the job in opposite ways and must
              never sit where a thumb aimed at one lands on the other. */}
          <div className="mt-5 pt-4 border-t border-sand">
            <button
              type="button"
              onClick={() => setCancelOpen(true)}
              className="fx-btn fx-btn--ghost text-sm text-danger"
              data-testid="job-cancel-cta"
            >
              <X size={16} /> {t("jobDetailX.cancelJob")}
            </button>
          </div>
        </div>
      )}

      {/* The reason, for the two people the job was between. The FixyBee was
          told it in the bell and the mail; this is where it stays. */}
      {job.status === "canceled" && job.cancel_reason && (isOwner || myQuote) && (
        <div className="mt-8 card-base p-5" data-testid="job-cancel-reason">
          <p className="text-sm text-worker">
            <strong>{t("jobDetailX.cancelModal.reason")}:</strong> {job.cancel_reason}
          </p>
        </div>
      )}

      {/* T16 M2. The way back into the review.

          Until now this modal opened at exactly one moment — the tap on "mark
          complete" — and closing it was final. A requester who dismissed it,
          reloaded the page, or confirmed completion from the push notification
          instead of from here had no second door: the review was lost, and they
          could not even find out whether they had already left one.

          Same modal, second door. Not a second way to review — `POST /reviews`
          reads for an existing row and `reviews.job_id` is UNIQUE, so one job
          carries one review whatever this page offers. `job.my_review` is that
          row, read for the owner only, and it is what makes this button
          disappear once there is nothing left to leave rather than open a form
          the server would refuse.

          Gated on `accepted_quote_id` because the endpoint is: a completed job
          with no accepted quote answers 400, and a button whose only possible
          answer is a refusal is worse than no button. */}
      {isOwner && job.status === "completed" && job.accepted_quote_id
        && !job.my_review && (
        <div className="mt-8 card-base p-5 bg-cream">
          <button
            onClick={() => setReviewOpen(true)}
            className="fx-btn fx-btn--primary text-sm"
            data-testid="leave-review-btn"
          >
            <Star size={16} /> {t("jobDetailX.leaveReview")}
          </button>
        </div>
      )}

      {reviewOpen && (
        <div className="fixed inset-0 bg-worker/60 z-50 flex items-center justify-center p-4" data-testid="review-modal">
          <div className="bg-cream rounded-md max-w-lg w-full p-6 shadow-deep">
            <h2 className="font-display text-2xl font-bold text-worker">{t("jobDetailX.reviewTitle")}</h2>
            <p className="text-smoke text-sm mt-1">{t("jobDetailX.reviewInstruction")}</p>

            {/* Two sections, told apart on screen, because they are two
                different things being asked for: the rating is required and is
                what moves the FixyBee's average, the written review is
                optional. Run together they read as one long form, and the
                optional half is what people stop at. */}
            <div className="mt-5 border-t border-sand pt-4">
              <p className="label-base">{t("jobDetailX.ratingSection")}</p>
              {dims.map((dim) => (
                <div key={dim.key} className="mt-3">
                  <p className="text-sm text-worker">{dim.label}</p>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        onClick={() => setReview({ ...review, [dim.key]: n })}
                        className="p-1"
                        aria-label={`${dim.label}: ${n}`}
                        data-testid={`rate-${dim.key}-${n}`}
                      >
                        {/* `review[dim.key]` is null until this dimension is
                            rated, and `n <= null` is false for every star —
                            which is the empty row this modal has to open on. */}
                        <Star size={28} className={review[dim.key] && n <= review[dim.key] ? "text-hive-ink fill-hive" : "text-sand"} />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 border-t border-sand pt-4">
              <p className="label-base">{t("jobDetailX.reviewSection")}</p>
              <textarea
                value={review.review_text}
                onChange={(e) => setReview({ ...review, review_text: e.target.value })}
                className="input-base mt-2 min-h-[80px]"
                placeholder={t("jobDetailX.optionalComment")}
                maxLength={1000}
                data-testid="review-text"
              />
            </div>

            {!reviewComplete && (
              <p className="text-smoke text-xs mt-4" data-testid="review-incomplete">
                {t("jobDetailX.rateAllFirst")}
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setReviewOpen(false)} className="fx-btn fx-btn--ghost">{t("jobDetailX.skip")}</button>
              <button onClick={submitReview} disabled={submitting || !reviewComplete} className="fx-btn fx-btn--primary" data-testid="submit-review">
                {submitting ? t("jobDetailX.savingDots") : t("jobDetailX.submitReview")}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelOpen && (
        <ConfirmModal
          testid="job-cancel-modal"
          title={t("jobDetailX.cancelModal.title")}
          body={t("jobDetailX.cancelModal.body", {
            name: acceptedQuote?.provider_snippet?.company_name
              || acceptedQuote?.provider_snippet?.full_name || "FixyBee",
          })}
          cta={t("jobDetailX.cancelModal.cta")}
          confirmDisabled={!reasonOk}
          onCancel={() => setCancelOpen(false)}
          onConfirm={cancelJob}
        >
          <label className="label-base mt-4 block" htmlFor="cancel-reason">
            {t("jobDetailX.cancelModal.reason")}
          </label>
          <textarea
            id="cancel-reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            className="input-base mt-1 min-h-[96px]"
            maxLength={500}
            data-testid="job-cancel-reason-input"
          />
          <p className="text-xs font-mono text-smoke mt-1">{reasonLength}/500</p>
        </ConfirmModal>
      )}

      {deleteOpen && (
        <ConfirmModal
          testid="job-delete-modal"
          title={t("jobDetailX.deleteModal.title")}
          body={t("jobDetailX.deleteModal.body")}
          cta={t("jobDetailX.deleteModal.cta")}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={deleteJob}
        />
      )}

      {disclaimerQuoteId && (
        <DisclaimerModal
          onCancel={() => setDisclaimerQuoteId(null)}
          onConfirm={() => acceptQuote(disclaimerQuoteId)}
        />
      )}
    </DashboardLayout>
  );
}

/**
 * T19. One confirmation, in the idiom of `DisclaimerModal` below: a sentence
 * that says what happens, the destructive button in the danger colour, and
 * Cancel beside it. `children` is the room for anything the act needs from
 * the person — the cancellation's reason — and `confirmDisabled` is how that
 * child holds the button until it is satisfied.
 *
 * `onConfirm` may throw; the server's own message is shown here rather than
 * the modal closing on a failure the person never sees.
 */
function ConfirmModal({ title, body, cta, onCancel, onConfirm, children, confirmDisabled = false, testid }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const confirm = async () => {
    setError("");
    setBusy(true);
    try {
      await onConfirm();
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-worker/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid={testid}>
      <div className="bg-cream rounded-md max-w-lg w-full p-6 relative shadow-deep">
        <button type="button" onClick={onCancel} className="absolute top-3 right-3 text-smoke hover:text-worker" aria-label={t("common.cancel")}>
          <X size={20} />
        </button>
        <h3 className="font-display text-2xl font-bold text-worker mb-3 pr-6">{title}</h3>
        <p className="text-sm text-worker/90 leading-relaxed">{body}</p>
        {children}
        {error && (
          <div className="mt-4 p-3 rounded-sm bg-danger/10 border border-danger/30 text-danger flex gap-2 items-start text-sm" data-testid={`${testid}-error`}>
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2 flex-wrap">
          <button onClick={onCancel} className="fx-btn fx-btn--ghost" data-testid={`${testid}-cancel`}>{t("common.cancel")}</button>
          <button
            onClick={confirm}
            disabled={busy || confirmDisabled}
            className="fx-btn fx-btn--primary bg-danger text-cream hover:bg-danger"
            data-testid={`${testid}-confirm`}
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : null}
            {cta}
          </button>
        </div>
      </div>
    </div>
  );
}

/** T9: mandatory liability disclaimer before a client accepts a quote.
 *  Confirming is what triggers POST /quotes/{id}/accept, which records the
 *  acceptance server-side in the same request. */
function DisclaimerModal({ onCancel, onConfirm }) {
  const { t } = useTranslation();
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const confirm = async () => {
    setError("");
    setBusy(true);
    try {
      await onConfirm();
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-worker/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="disclaimer-modal">
      <div className="bg-cream rounded-md max-w-lg w-full p-6 relative shadow-deep">
        <button type="button" onClick={onCancel} className="absolute top-3 right-3 text-smoke hover:text-worker" data-testid="close-disclaimer-modal">
          <X size={20} />
        </button>
        <h3 className="font-display text-2xl font-bold text-worker mb-3">{t("jobDetailX.acceptQuote")}</h3>

        <p className="text-sm text-worker/90 leading-relaxed" data-testid="disclaimer-body">
          {t("legal.disclaimer.body")}
        </p>

        {error && (
          <div className="mt-4 p-3 rounded-sm bg-danger/10 border border-danger/30 text-danger flex gap-2 items-start text-sm" data-testid="disclaimer-error">
            <AlertCircle size={16} className="mt-0.5" /> {error}
          </div>
        )}

        <label className="mt-5 flex items-start gap-2 text-sm text-worker cursor-pointer" data-testid="disclaimer-label">
          <input
            type="checkbox"
            checked={understood}
            onChange={(e) => setUnderstood(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-hive-deep"
            data-testid="disclaimer-checkbox"
          />
          <span className="font-semibold">{t("legal.disclaimer.accept")}</span>
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="fx-btn fx-btn--ghost" data-testid="disclaimer-cancel">{t("common.cancel")}</button>
          <button
            onClick={confirm}
            disabled={!understood || busy}
            className="fx-btn fx-btn--primary"
            data-testid="disclaimer-confirm"
          >
            {busy ? <><Loader2 className="animate-spin" size={16} /> {t("jobDetailX.savingDots")}</> : t("jobDetailX.acceptQuote")}
          </button>
        </div>
      </div>
    </div>
  );
}
